import React, { useState, useMemo } from 'react';

/**
 * Sortable, filterable data table for container data.
 * Supports per-column dropdown filters and a text search box.
 * Columns can specify format: 'date' for date formatting, 'dwell' for color-coded dwell days.
 */
export default function DataTable({ columns, rows, note, globalFilter }) {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [textFilter, setTextFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState({}); // { colKey: Set of selected values }

  // Build unique values for each column (for dropdown filters)
  const columnValues = useMemo(() => {
    const vals = {};
    columns.forEach((col) => {
      const unique = new Set();
      rows.forEach((row) => {
        const v = col.format === 'date' ? formatDate(row[col.key]) : String(row[col.key] || '');
        if (v) unique.add(v);
      });
      vals[col.key] = [...unique].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    });
    return vals;
  }, [rows, columns]);

  // Apply global filter first (from parent search)
  const globalFiltered = useMemo(() => {
    if (!globalFilter) return rows;
    const q = globalFilter.toLowerCase();
    return rows.filter((row) =>
      columns.some((col) => String(row[col.key] || '').toLowerCase().includes(q))
    );
  }, [rows, globalFilter, columns]);

  // Apply text filter
  const textFiltered = useMemo(() => {
    if (!textFilter) return globalFiltered;
    const q = textFilter.toLowerCase();
    return globalFiltered.filter((row) =>
      columns.some((col) => String(row[col.key] || '').toLowerCase().includes(q))
    );
  }, [globalFiltered, textFilter, columns]);

  // Apply column filters
  const filtered = useMemo(() => {
    let result = textFiltered;
    Object.entries(columnFilters).forEach(([colKey, selectedSet]) => {
      if (selectedSet && selectedSet.size > 0) {
        const col = columns.find((c) => c.key === colKey);
        result = result.filter((row) => {
          const v = col && col.format === 'date' ? formatDate(row[colKey]) : String(row[colKey] || '');
          return selectedSet.has(v);
        });
      }
    });
    return result;
  }, [textFiltered, columnFilters, columns]);

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    return [...filtered].sort((a, b) => {
      const aVal = a[sortKey] ?? '';
      const bVal = b[sortKey] ?? '';
      const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const toggleColumnFilter = (colKey, value) => {
    setColumnFilters((prev) => {
      const existing = prev[colKey] ? new Set(prev[colKey]) : new Set();
      if (existing.has(value)) {
        existing.delete(value);
      } else {
        existing.add(value);
      }
      return { ...prev, [colKey]: existing };
    });
  };

  const clearColumnFilter = (colKey) => {
    setColumnFilters((prev) => {
      const next = { ...prev };
      delete next[colKey];
      return next;
    });
  };

  const activeFilterCount = Object.values(columnFilters).filter((s) => s && s.size > 0).length;

  if (rows.length === 0) {
    return <p className="text-gray-500 text-center py-8">No containers in this category.</p>;
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <input
          type="text"
          placeholder="Filter this table..."
          value={textFilter}
          onChange={(e) => setTextFilter(e.target.value)}
          className="px-3 py-2 border rounded w-64 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
        />
        <span className="text-sm text-gray-500">{sorted.length} rows</span>
        {activeFilterCount > 0 && (
          <button
            onClick={() => setColumnFilters({})}
            className="text-xs text-red-500 hover:text-red-700 underline"
          >
            Clear all filters ({activeFilterCount})
          </button>
        )}
      </div>
      {note && <div className="text-xs text-gray-400 mb-2">{note}</div>}

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 border-b">
              {columns.map((col) => (
                <th key={col.key} className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                  <div className="flex items-center gap-1">
                    <span
                      className="cursor-pointer hover:text-gray-800"
                      onClick={() => handleSort(col.key)}
                    >
                      {col.label}
                      {sortKey === col.key && (sortDir === 'asc' ? ' ▲' : ' ▼')}
                    </span>
                    <ColumnFilterDropdown
                      colKey={col.key}
                      values={columnValues[col.key] || []}
                      selected={columnFilters[col.key]}
                      onToggle={(val) => toggleColumnFilter(col.key, val)}
                      onClear={() => clearColumnFilter(col.key)}
                    />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sorted.map((row, idx) => (
              <tr key={idx} className="hover:bg-gray-50">
                {columns.map((col) => (
                  <td key={col.key} className="px-2 py-1 whitespace-nowrap">
                    {col.format === 'dwell' ? (
                      <DwellBadge value={row[col.key]} />
                    ) : col.format === 'date' ? (
                      formatDate(row[col.key])
                    ) : (
                      row[col.key] || ''
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ColumnFilterDropdown({ colKey, values, selected, onToggle, onClear }) {
  const [open, setOpen] = useState(false);
  const hasFilter = selected && selected.size > 0;

  if (values.length === 0) return null;
  // Don't show filter for columns with too many unique values (like booking #)
  if (values.length > 100) return null;

  return (
    <div className="relative inline-block">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className={`ml-0.5 text-xs ${hasFilter ? 'text-amber-600' : 'text-gray-400'} hover:text-gray-700`}
        title="Filter column"
      >
        ▼
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded shadow-lg max-h-48 overflow-y-auto min-w-[140px]">
            {hasFilter && (
              <button
                onClick={() => { onClear(); setOpen(false); }}
                className="w-full text-left px-2 py-1 text-xs text-red-500 hover:bg-red-50 border-b"
              >
                Clear filter
              </button>
            )}
            {values.map((val) => (
              <label
                key={val}
                className="flex items-center gap-1.5 px-2 py-1 text-xs hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selected ? selected.has(val) : false}
                  onChange={() => onToggle(val)}
                  className="rounded text-amber-500"
                />
                <span className="truncate">{val}</span>
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function DwellBadge({ value }) {
  if (value == null || value === '') return '';
  const n = Number(value);
  let color = 'bg-green-100 text-green-800';  // < 3
  if (n >= 7) color = 'bg-red-100 text-red-800';
  else if (n >= 3) color = 'bg-yellow-100 text-yellow-800';

  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      {n}
    </span>
  );
}

function formatDate(val) {
  if (!val) return '';
  // Handle ISO dates like "2026-03-19T14:00:00Z" or "2026-03-19"
  try {
    let dateStr = val;
    if (dateStr.includes('T')) dateStr = dateStr.split('T')[0];
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const yy = parts[0].slice(-2);
      return `${parts[1]}/${parts[2]}/${yy}`;
    }
  } catch (e) {}
  return val;
}
